import { faArrowDown, faArrowUp, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { FunctionComponent, useMemo, useState } from 'react';
import styles from './WeightHistoryModal.module.scss';

interface WeightHistoryModalProps {
    open: boolean;
    petName: string;
    petWeight?: number | string;
    onClose: () => void;
}

type ChangeDirection = 'increase' | 'decrease' | 'same' | 'initial';

interface WeightHistoryEntry {
    date: string;
    weight: string;
    change: string;
    direction: ChangeDirection;
    staff: string;
}

const HISTORY_DATES = [
    'Aug 11, 2026',
    'Aug 10, 2026',
    'Aug 09, 2026',
    'Aug 08, 2026',
    'Aug 07, 2026',
    'Aug 06, 2026',
    'Aug 05, 2026',
    'Aug 04, 2026',
    'Aug 03, 2026',
    'Aug 02, 2026',
    'Aug 01, 2026',
    'Jul 31, 2026'
];

// Offsets keep the mock history recognizable while scaling around the pet's real current weight.
const WEIGHT_OFFSETS = [0, 1, 0, 0.3, 1.3, -0.7, 0, -2, -2, -2.5, -2, -3];

const formatNumber = (value: number) => (Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1));

const buildWeightHistory = (currentWeight?: number | string): WeightHistoryEntry[] => {
    const parsedWeight = Number(currentWeight);
    const baseWeight = Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : 32;
    const weights = WEIGHT_OFFSETS.map(offset => Number(Math.max(0.1, baseWeight + offset).toFixed(1)));

    return weights.map((weight, index) => {
        const isInitial = index === weights.length - 1;
        const difference = isInitial ? 0 : Number((weight - weights[index + 1]).toFixed(1));
        const direction: ChangeDirection = isInitial
            ? 'initial'
            : difference > 0
            ? 'increase'
            : difference < 0
            ? 'decrease'
            : 'same';
        const change = isInitial
            ? 'Initial'
            : difference === 0
            ? 'No change'
            : `${difference > 0 ? '+' : ''}${formatNumber(difference)} Lbs`;

        return {
            date: HISTORY_DATES[index],
            weight: `${formatNumber(weight)} Lbs`,
            change,
            direction,
            staff: 'Staff Name'
        };
    });
};

const WeightHistoryModal: FunctionComponent<WeightHistoryModalProps> = ({ open, petName, petWeight, onClose }) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const weightHistory = useMemo(() => buildWeightHistory(petWeight), [petWeight]);
    const entries = useMemo(
        () => weightHistory.slice((page - 1) * pageSize, page * pageSize),
        [page, pageSize, weightHistory]
    );

    const changePageSize = (nextPageSize: number) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    const changeBadgeClass = (direction: ChangeDirection) =>
        direction === 'increase' ? styles.increase : direction === 'decrease' ? styles.decrease : styles.neutral;

    const changeIcon = (direction: ChangeDirection) => {
        if (direction === 'increase') return faArrowUp;
        if (direction === 'decrease') return faArrowDown;
        if (direction === 'same') return faMinus;
        return undefined;
    };

    if (!open) return null;

    const pagination = (
        <Pagination
            className={styles.pagination}
            page={page}
            totalPages={Math.ceil(weightHistory.length / pageSize)}
            size={pageSize}
            onPageChange={setPage}
            onSizeChange={changePageSize}
        />
    );

    return (
        <GenericModal
            className={styles.modal}
            onClose={onClose}
            footer={pagination}
        >
            <div className={styles.content}>
                <h2
                    id="weight-history-title"
                    className={styles.title}
                >
                    Weight History - {petName}
                </h2>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.date}>Date</th>
                            <th className={styles.weight}>Weight</th>
                            <th className={styles.change}>Change</th>
                            <th className={styles.staff}>Staff</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(entry => {
                            const icon = changeIcon(entry.direction);
                            return (
                                <tr key={entry.date}>
                                    <td>{entry.date}</td>
                                    <td>{entry.weight}</td>
                                    <td>
                                        <span className={`${styles.badge} ${changeBadgeClass(entry.direction)}`}>
                                            {entry.change}
                                            {icon && <FontAwesomeIcon icon={icon} />}
                                        </span>
                                    </td>
                                    <td>{entry.staff}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </GenericModal>
    );
};

export default WeightHistoryModal;
